import { TaskStatusTypeEnum } from '@zaoblako/shared/modules/task/enum/task.status.type.enum';
import { TaskStatusTypeEnumI18n } from '@zaoblako/shared/modules/task/i18n/task.status.type.enum.i18n';
import { I18nLocalesEnum } from '@zaoblako/shared/common/enums/i18n.locales.enum';
import { TaskPaymentTypeEnum } from '@zaoblako/shared/modules/task/enum/task.payment.type.enum';
import { TaskPaymentTypeEnumI18n } from '@zaoblako/shared/modules/task/i18n/task.payment.type.enum.i18n';
import { TaskKindEnum } from '@zaoblako/shared/modules/task/enum/task-kind.enum';
import { TaskKindEnumI18n } from '@zaoblako/shared/modules/task/i18n/task-kind-enum.i18n';
import { TaskOrderFormDto, type TaskOrderFormInterface } from '@zaoblako/shared/modules/task/dtos/order/task-order-form.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { TaskDtoKeysEnum } from '@zaoblako/shared/modules/task/enum/task-dto-keys.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { booleanSchema } from '@zaoblako/shared/modules/serde/schemas/boolean.schema';
import { entityLikeSchema, entityLikeSchemaUuid, entityLikeSchemaUuidStrict } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';
import { ClientCheckerKeysEnum } from '@zaoblako/shared/modules/client/enums/client-checker-keys.enum';
import { TaskCheckersKeysEnum } from '@zaoblako/shared/modules/task/enum/task-checkers-keys.enum';
import { StorageBackendValidatorsEnum } from '@zaoblako/shared/modules/storage/enums/storage-backend-validators.enum';
import { SecurityBackendValidatorsEnum } from '@zaoblako/shared/modules/security/enums/security-backend-validators.enum';
import { BusinessCheckerKeysEnum } from '@zaoblako/shared/modules/business/enum/business-checker-keys.enum';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { SerdeContextDataInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context-data.interface';
import type { SerdeContextInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context.interface';
import type { ApprovalSchemeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { TaskPriorityFindInterface } from '@zaoblako/shared/modules/task/dtos/task-priority-find.dto';
import type { TaskStatusFindInterface } from '@zaoblako/shared/modules/task/dtos/status/task-status-find.dto';
import type { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import type { NdsFindInterface } from '@zaoblako/shared/modules/finance/dtos/nds-find.dto';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file/file-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { ContractFindInterface } from '@zaoblako/shared/modules/document/dtos/contract/contract-find.dto';
import type { CostCenterFindInterface } from '@zaoblako/shared/modules/catalog/dtos/cost-center/cost-center-find.dto';
import type { BudgetClassificationCodeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/budget-classification-code/budget-classification-code-find.dto';
import type { PayerStatusFindInterface } from '@zaoblako/shared/modules/catalog/dtos/payer-status/payer-status-find.dto';
import type { BankDetailsFindInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank-details-find.dto';

import * as yup from 'yup';

/** BRS task form usage operation type: task creation or updating */
export type TaskFormOperation = 'create' | 'update';

/** Validation context for BRS task form */
export interface TaskFormDtoContextInterface extends SerdeContextDataInterface {
  /** Form operation type */
  operation?: TaskFormOperation;
  /** Current or initial BRS task status type */
  status?: TaskStatusTypeEnum;
}

/** BRS task create or update request body - DTO signature */
export interface TaskFormInterface {
  /** Payment date */
  date?: Date;
  /** Payment subject */
  subject?: string;
  /** Payment amount */
  amount?: number;
  /** Value Added Tax (VAT, rus. "НДС") amount */
  vat?: number;
  /** Payment type */
  type?: TaskPaymentTypeEnum;
  /** Payment kind */
  kind?: TaskKindEnum;
  /** Commentary (only on task creation) */
  commentary?: string;
  /** Approval scheme */
  scheme?: ApprovalSchemeFindInterface;
  /** Counterparty (recipient of payment) */
  contragent?: ContragentFindInterface;
  /** Organization (payer) bank details */
  contragentBankDetails?: BankDetailsFindInterface;
  /** Organization (payer) */
  organization?: CompanyFindInterface;
  /** Organization (payer) bank details */
  organizationBankDetails?: BankDetailsFindInterface;
  /** Related unit */
  unit?: UnitFindInterface;
  /** Related filial */
  filial?: FilialFindInterface;
  /** Related direction */
  direction?: DirectionFindInterface;
  /** BRS task priority */
  priority?: TaskPriorityFindInterface;
  /** BRS task initial status */
  status?: TaskStatusFindInterface;
  /** BRS task currency ("Russian Ruble" by default) */
  currency?: CurrencyFindInterface;
  /** BRS task Value Added Tax (VAT, rus. "НДС") rate */
  nds?: NdsFindInterface;
  /** BRS task orders */
  orders?: TaskOrderFormInterface[];
  /** BRS task initial files to attach */
  files?: FileFindInterface[];
  /** BRS task responsible user */
  responsible?: UserFindInterface;
  /** BRS task aditors users */
  userAuditors?: UserFindInterface[];
  /** BRS task aditors user groups */
  userGroupAuditors?: UserGroupFindInterface[];
  /** Related deal */
  deal?: DealFindInterface;
  /** Related contract */
  contract?: ContractFindInterface;
  /** Related cost center */
  costCenter?: CostCenterFindInterface;
  /** Budget classification code, using only when {@link TaskFormInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  budgetClassificactionCode?: BudgetClassificationCodeFindInterface;
  /** Municipality classification code (rus. "ОКТМО"), using only when {@link TaskFormInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  municipalityClassificationCode?: string;
  /** Payer status, using only when {@link TaskFormInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  payerStatus?: PayerStatusFindInterface;
  /** Payment unique ID for 1C ERP databases */
  paymentId?: string;
  /** Unique Accrual Identifier (rus. "УИН"), using only when {@link TaskFormInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  uniqueAccrualIdentifier?: string;
  /** Payment reason, using only when {@link TaskFormInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  paymentReason?: string;
  /** Tax period, using only when {@link TaskFormInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  taxPeriod?: string;
  /** Document number, using only when {@link TaskFormInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  documentNumber?: string;
  /** Document date, using only when {@link TaskFormInterface.kind} equals {@link TaskKindEnum.PAYMENT_OF_TAXES} */
  documentDate?: Date;
  /** Payment by credit funds */
  creditPayment: boolean;
}

const notOnStatus = (...types: TaskStatusTypeEnum[]) => (
  context: SerdeContextInterface<TaskFormDtoContextInterface, TaskFormInterface>,
): boolean => !types.includes(context?.data?.status);

const notOnKind = (...kinds: TaskKindEnum[]) => (
  context: SerdeContextInterface<TaskFormDtoContextInterface, TaskFormInterface>,
): boolean => !kinds.includes(context?.value?.kind);

interface SchemaOnKindParams extends Partial<Record<TaskKindEnum, yup.Schema<any>>> {
  /** Default schema */
  byDefault: yup.Schema<any>;
}

const schemaOnKind = (params: SchemaOnKindParams) => (
  context: SerdeContextInterface<TaskFormDtoContextInterface, TaskFormInterface>,
): yup.Schema<any> => params[context?.value?.kind] || params.byDefault;

/** {@link notOnStatusAndKind} parameters. For internal usage only. */
interface NotOnStatusAndKindParams extends Partial<Record<TaskKindEnum, TaskStatusTypeEnum[]>> {
  /** Default status types */
  byDefault: TaskStatusTypeEnum[];
}

const notOnStatusAndKind = (params: NotOnStatusAndKindParams) => (
  context: SerdeContextInterface<TaskFormDtoContextInterface, TaskFormInterface>,
): boolean => {
  const types = params[context?.value?.kind] || params.byDefault;
  return !types.includes(context?.data?.status);
};

const hintRequiredOn = (...types: TaskStatusTypeEnum[]) => (
  context: SerdeContextInterface<TaskFormDtoContextInterface, TaskFormInterface>,
): string => `Поле обязательно для заявки со статусами: ${
  types.map(type => `"${TaskStatusTypeEnumI18n[context?.data?.locale || I18nLocalesEnum.RU_RU][type]}"`)
}`;

/** BRS task create or update request body - DTO schema */
export const TaskFormDto = SerdeDtoClass.create<TaskFormInterface, TaskFormDtoContextInterface>(TaskDtoKeysEnum.TASK_FORM_DTO, {
  date: {
    optional: notOnStatus(TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    label: 'Дата оплаты',
    hint: hintRequiredOn(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    serializer: yup.date(),
  },
  subject: {
    optional: notOnStatus(TaskStatusTypeEnum.ON_APPROVAL),
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    label: 'Назначение платежа',
    hint: hintRequiredOn(TaskStatusTypeEnum.ON_APPROVAL),
    serializer: yup
      .string()
      .min(1, DtoMessageEnum.EMPTY_STRING),
  },
  amount: {
    optional: notOnStatus(TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED),
    label: 'Сумма платежа',
    hint: hintRequiredOn(TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    serializer: yup
      .number()
      .positive(DtoMessageEnum.NOT_POSITIVE),
  },
  vat: {
    optional: notOnStatus(TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED),
    label: 'Сумма НДС',
    hint: hintRequiredOn(TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    serializer: yup
      .number()
      .min(0, DtoMessageEnum.NOT_ZERO_OR_POSITIVE),
  },
  type: {
    optional: notOnStatus(TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    label: 'Тип платежа',
    hint: hintRequiredOn(TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    serializer: commonEnumValidator(TaskPaymentTypeEnum, TaskPaymentTypeEnumI18n),
  },
  kind: {
    optional: true,
    default: TaskKindEnum.PAYMENT_TO_SUPPLIER,
    label: 'Вид платежа',
    serializer: commonEnumValidator(TaskKindEnum, TaskKindEnumI18n),
  },
  commentary: {
    optional: true,
    hidden: (
      context: SerdeContextInterface<TaskFormDtoContextInterface, TaskFormInterface>,
    ): boolean => context?.data?.operation !== 'create',
    disabled: (
      context: SerdeContextInterface<TaskFormDtoContextInterface, TaskFormInterface>,
    ): boolean => context?.data?.operation !== 'create',
    label: 'Комментарий',
    serializer: yup.string(),
  },
  scheme: {
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    optional: notOnStatus(TaskStatusTypeEnum.ON_APPROVAL),
    label: 'Схема согласования',
    hint: hintRequiredOn(TaskStatusTypeEnum.ON_APPROVAL),
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.APPROVAL_SCHEME_ASSERT_EXISTS_BY_DTO],
  },
  contragent: {
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    optional: notOnStatus(TaskStatusTypeEnum.ON_APPROVAL),
    label: 'Контрагент',
    serializer: entityLikeSchema,
    checkers: [ClientCheckerKeysEnum.CONTRAGENT_ASSERT_EXISTS_BY_DTO],
  },
  contragentBankDetails: {
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    optional: notOnStatus(TaskStatusTypeEnum.ON_APPROVAL),
    label: 'Рассчетный счет контрагента',
    serializer: entityLikeSchema,
    checkers: [ClientCheckerKeysEnum.BANK_DETAILS_ASSERT_EXISTS_BY_DTO],
  },
  organization: {
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    optional: notOnStatus(TaskStatusTypeEnum.ON_APPROVAL),
    label: 'Плательщик',
    hint: hintRequiredOn(TaskStatusTypeEnum.ON_APPROVAL),
    serializer: entityLikeSchema,
    checkers: [ClientCheckerKeysEnum.COMPANY_ASSERT_EXISTS_BY_DTO],
  },
  organizationBankDetails: {
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    optional: notOnStatus(TaskStatusTypeEnum.ON_APPROVAL),
    label: 'Рассчетный счет плательщика',
    serializer: entityLikeSchema,
    checkers: [ClientCheckerKeysEnum.BANK_DETAILS_ASSERT_EXISTS_BY_DTO],
  },
  unit: {
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    optional: context => (
      [TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED].includes(context?.data?.status) ||
      !context?.data?.requiredAnalyticalFeatures?.unitsRequired
    ),
    label: 'БЕ',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.UNIT_ASSERT_EXISTS_BY_DTO],
  },
  filial: {
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    optional: context => (
      [TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED].includes(context?.data?.status) ||
      !context?.data?.requiredAnalyticalFeatures?.filialsRequired
    ),
    label: 'Филиал',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.FILIAL_ASSERT_EXISTS_BY_DTO],
  },
  direction: {
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    optional: context => (
      [TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED].includes(context?.data?.status) ||
      !context?.data?.requiredAnalyticalFeatures?.directionsRequired
    ),
    label: 'БС',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.DIRECTION_ASSERT_EXISTS_BY_DTO],
  },
  priority: {
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    optional: notOnStatus(TaskStatusTypeEnum.ON_APPROVAL),
    label: 'Приоритет',
    hint: hintRequiredOn(TaskStatusTypeEnum.ON_APPROVAL),
    serializer: entityLikeSchema,
    checkers: [TaskCheckersKeysEnum.TASK_PRIORITY_ASSERT_EXISTS_BY_DTO],
  },
  status: {
    optional: true,
    hidden: (
      context: SerdeContextInterface<TaskFormDtoContextInterface, TaskFormInterface>,
    ): boolean => context?.data?.operation !== 'create',
    disabled: (
      context: SerdeContextInterface<TaskFormDtoContextInterface, TaskFormInterface>,
    ): boolean => context?.data?.operation !== 'create',
    label: 'Начальный статус',
    hint: 'Требуется только при создании заявки',
    serializer: entityLikeSchema,
  },
  currency: {
    optional: true,
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    label: 'Валюта',
    serializer: entityLikeSchemaUuid,
    checkers: [CatalogCheckersEnum.CURRENCY_ASSERT_EXISTS_BY_DTO],
  },
  nds: {
    optional: notOnStatus(TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    label: 'Ставка НДС',
    serializer: entityLikeSchema,
    checkers: [TaskCheckersKeysEnum.NDS_ASSERT_EXISTS_BY_DTO],
  },
  orders: {
    optional: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatusAndKind({
      byDefault: [
        TaskStatusTypeEnum.DRAFT,
        TaskStatusTypeEnum.SCHEDULED,
        TaskStatusTypeEnum.ON_APPROVAL,
      ],
      [TaskKindEnum.PAYMENT_OF_SALARY]: [
        TaskStatusTypeEnum.DRAFT,
        TaskStatusTypeEnum.SCHEDULED,
        TaskStatusTypeEnum.ON_APPROVAL,
        TaskStatusTypeEnum.AGREED,
      ],
    }),
    label: 'Платежные поручения',
    nested: TaskOrderFormDto,
    serializer: (_, nested) => yup
      .array(nested)
      .min(1, DtoMessageEnum.EMPTY_ARRAY),
  },
  files: {
    optional: true,
    hidden: (
      context: SerdeContextInterface<TaskFormDtoContextInterface, TaskFormInterface>,
    ): boolean => context?.data?.operation !== 'create',
    disabled: (
      context: SerdeContextInterface<TaskFormDtoContextInterface, TaskFormInterface>,
    ): boolean => context?.data?.operation !== 'create',
    label: 'Файлы',
    hint: 'Учитываются только при создании заявки',
    serializer: yup.array(entityLikeSchemaUuid),
    checkers: [StorageBackendValidatorsEnum.FILE_ASSERT_EXISTS_BY_DTO],
  },
  responsible: {
    optional: true,
    label: 'Ответственный',
    serializer: entityLikeSchema,
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
  },
  userAuditors: {
    optional: true,
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    label: 'Пользователи-аудиторы',
    serializer: yup.array(entityLikeSchema),
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
  },
  userGroupAuditors: {
    optional: true,
    hidden: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    disabled: notOnStatus(TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL),
    label: 'Группы пользователей-аудиторов',
    serializer: yup.array(entityLikeSchema),
    checkers: [SecurityBackendValidatorsEnum.USER_GROUP_ASSERT_EXISTS_BY_DTO],
  },
  deal: {
    optional: true,
    hidden: false,
    disabled: false,
    label: 'Сделка',
    serializer: entityLikeSchema,
    checkers: [BusinessCheckerKeysEnum.DEAL_ASSERT_EXISTS_BY_DTO],
  },
  contract: {
    optional: true,
    hidden: false,
    disabled: false,
    label: 'Договор',
    serializer: entityLikeSchema,
  },
  costCenter: {
    optional: true,
    hidden: false,
    disabled: false,
    label: 'Центр затрат',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.COST_CENTER_ASSERT_EXISTS_BY_DTO],
  },
  budgetClassificactionCode: {
    optional: notOnKind(TaskKindEnum.PAYMENT_OF_TAXES),
    label: 'КБК',
    serializer: schemaOnKind({
      byDefault: entityLikeSchemaUuid,
      [TaskKindEnum.PAYMENT_OF_TAXES]: entityLikeSchemaUuidStrict,
    }),
    checkers: [CatalogCheckersEnum.BUDGET_CLASSIFICATION_CODE_ASSERT_EXISTS_BY_DTO],
  },
  municipalityClassificationCode: {
    optional: true,
    label: 'ОКТМО',
    serializer: yup
      .string()
      .matches(/^(\d{8}|\d{11})$/i, 'Код ОКТМО должен состоять из 8 или 11 цифр'),
  },
  payerStatus: {
    optional: notOnKind(TaskKindEnum.PAYMENT_OF_TAXES),
    label: 'Статус плательщика',
    serializer: schemaOnKind({
      byDefault: entityLikeSchemaUuid,
      [TaskKindEnum.PAYMENT_OF_TAXES]: entityLikeSchemaUuidStrict,
    }),
    checkers: [CatalogCheckersEnum.PAYER_STATUS_ASSERT_EXISTS_BY_DTO],
  },
  paymentId: {
    optional: true,
    label: 'Идентификатор платежа',
    serializer: yup.string(),
  },
  uniqueAccrualIdentifier: {
    optional: true,
    label: 'УИН',
    serializer: yup
      .string()
      .matches(/^(\d{20}|\d{25})$/i, 'УИН должен состояить из 20 или 25 цифр'),
  },
  paymentReason: {
    optional: true,
    label: 'Основание платежа',
    serializer: yup.string(),
  },
  taxPeriod: {
    optional: true,
    label: 'Налоговый период',
    serializer: yup.string(),
  },
  documentNumber: {
    optional: true,
    label: 'Номер документа',
    serializer: yup.string(),
  },
  documentDate: {
    optional: true,
    label: 'Дата документа',
    serializer: yup.date(),
  },
  creditPayment: {
    optional: true,
    label: 'Оплата через кредитные средства',
    serializer: booleanSchema,
  },
});
